import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import UpcomingOutlinedIcon from "@mui/icons-material/UpcomingOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SportsOutlinedIcon from "@mui/icons-material/SportsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import {
  FEATURE_DESCRIPTIONS,
  FeatureMap,
  featureLabel,
  getFeatureKeys,
  getOrganizationStats,
  OrganizationStats,
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PlatformKey,
  setOrganizationPlatform,
  setOrganizationPublicBrowse,
  updateOrganizationFeatures,
} from "../../services/superadminService";
import SubscriptionPanel from "../../components/superadmin/SubscriptionPanel";
import OrganizationDangerZone from "../../components/superadmin/OrganizationDangerZone";
import NotificationPanel from "../../components/superadmin/NotificationPanel";
import {
  PlatformChip,
  SubscriptionStatusChip,
} from "../../components/superadmin/SubscriptionChips";
import {
  IconTile,
  KpiCard,
  KpiGrid,
  SA,
  SaCard,
  SaSectionNav,
  SettingRow,
  StatusPill,
  TONES,
  Tone,
  saCardSx,
} from "../../components/superadmin/saUi";
import { getErrorMessage } from "../../utils/apiResponse";

const WINDOWS = [7, 30, 90];

const TABS = [
  { key: "overview", label: "Overview", icon: <InsightsOutlinedIcon fontSize="small" /> },
  { key: "subscription", label: "Subscription", icon: <WorkspacePremiumOutlinedIcon fontSize="small" /> },
  { key: "messages", label: "Messages", icon: <ForumOutlinedIcon fontSize="small" /> },
  { key: "settings", label: "Settings", icon: <TuneOutlinedIcon fontSize="small" /> },
  { key: "danger", label: "Danger zone", icon: <ReportGmailerrorredOutlinedIcon fontSize="small" />, danger: true },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const FEATURE_ICONS: Record<string, React.ReactNode> = {
  LOYALTY: <CardGiftcardOutlinedIcon />,
  CLASSES: <GroupsOutlinedIcon />,
  STOCK: <Inventory2OutlinedIcon />,
};

/**
 * Reserved status colors — never reused as generic series colors. Validated for
 * CVD separation and 3:1 contrast against a light surface. Unknown statuses fall
 * back to the neutral, and every segment carries a written label, so identity is
 * never colour-alone.
 */
const STATUS_COLORS: Record<string, string> = {
  DONE: "#1D8A57",
  COMPLETED: "#1D8A57",
  CONFIRMED: "#1D8A57",
  PENDING: "#BC8A00",
  CANCELLED: "#C4362A",
  CANCELED: "#C4362A",
};
const NEUTRAL_STATUS = "#6B7280";

const statusColor = (status: string) =>
  STATUS_COLORS[status.toUpperCase()] || NEUTRAL_STATUS;

const prettyStatus = (status: string) =>
  status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("") || "?";

/** Proportional breakdown, one segment per status actually present. */
const StatusBreakdown: React.FC<{ byStatus: Record<string, number> }> = ({
  byStatus,
}) => {
  const entries = Object.entries(byStatus || {}).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  if (!total) {
    return (
      <Typography sx={{ color: SA.textMuted, fontSize: "0.9rem" }}>
        No bookings in this window.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: "3px",
          height: 10,
          mb: 2,
          width: "100%",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        {entries.map(([status, count]) => (
          <Tooltip
            key={status}
            title={`${prettyStatus(status)}: ${count} (${Math.round(
              (count / total) * 100
            )}%)`}
          >
            <Box sx={{ width: `${(count / total) * 100}%`, bgcolor: statusColor(status) }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {entries.map(([status, count]) => (
          <Box key={status} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: statusColor(status) }} />
            <Typography sx={{ fontSize: "0.86rem", color: SA.text, fontWeight: 500 }}>
              {prettyStatus(status)}
            </Typography>
            <Typography sx={{ fontSize: "0.86rem", color: SA.textMuted, fontVariantNumeric: "tabular-nums" }}>
              {count} · {Math.round((count / total) * 100)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** A label and its number on one line, for the smaller count lists. */
const MiniStat: React.FC<{
  icon: React.ReactNode;
  tone: Tone;
  label: string;
  value: React.ReactNode;
  caption?: string;
  divider?: boolean;
}> = ({ icon, tone, label, value, caption, divider }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.25,
      borderTop: divider ? `1px solid ${SA.divider}` : 0,
    }}
  >
    <IconTile icon={icon} tone={tone} size={30} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.88rem", color: SA.text, fontWeight: 500 }}>{label}</Typography>
      {caption && <Typography sx={{ fontSize: "0.76rem", color: SA.textMuted }}>{caption}</Typography>}
    </Box>
    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: SA.text, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Typography>
  </Box>
);

/** One fact in the header's summary strip. */
const HeaderFact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.75rem", color: SA.textMuted, mb: 0.5 }}>{label}</Typography>
    <Box sx={{ fontSize: "1.05rem", fontWeight: 700, color: SA.text, fontVariantNumeric: "tabular-nums" }}>
      {children}
    </Box>
  </Box>
);

const MetaItem: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      color: SA.textMuted,
      fontSize: "0.85rem",
      "& svg": { fontSize: 16, color: SA.textSubtle },
    }}
  >
    {icon}
    {children}
  </Box>
);

const pillToggleSx = {
  bgcolor: SA.surface,
  borderRadius: "10px",
  boxShadow: SA.shadow,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 1.75,
    py: 0.5,
    fontWeight: 600,
    color: SA.textMuted,
    borderColor: SA.border,
    "&.Mui-selected": { bgcolor: TONES.blue.bg, color: TONES.blue.fg },
  },
} as const;

const OrganizationDetails: React.FC = () => {
  const { organizationId = "" } = useParams();
  const navigate = useNavigate();
  // In the URL so a refresh or the back button keeps the open tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab: TabKey = TABS.some((t) => t.key === requestedTab)
    ? (requestedTab as TabKey)
    : "overview";
  const openTab = (key: TabKey) =>
    setSearchParams(key === "overview" ? {} : { tab: key }, { replace: true });
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [features, setFeatures] = useState<FeatureMap>({});
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [publicBrowse, setPublicBrowse] = useState(false);
  const [savingPublicBrowse, setSavingPublicBrowse] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFeatureKeys()
      .then(setFeatureKeys)
      .catch(() => setFeatureKeys([]));
  }, []);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const result = await getOrganizationStats(organizationId, days);
      setStats(result);
      setFeatures(result.features || {});
      setPublicBrowse(!!result.organization?.publicBrowseEnabled);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [organizationId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (key: string, enabled: boolean) => {
    const previous = features;
    setFeatures((prev) => ({ ...prev, [key]: enabled })); // optimistic
    setSavingKey(key);
    try {
      const result = await updateOrganizationFeatures(organizationId, [
        { feature: key, enabled },
      ]);
      // Reconcile from the full map the API returns, not from the guess.
      setFeatures(result.features);
      toast.success(
        `${featureLabel(key)} ${enabled ? "enabled" : "hidden"} for ${
          stats?.organization?.name ?? "this organization"
        }.`,
        { position: "top-right" }
      );
    } catch (err: any) {
      setFeatures(previous); // roll back
      toast.error(getErrorMessage(err, "Could not update the feature."), {
        position: "top-right",
      });
    }
    setSavingKey(null);
  };

  const handlePublicBrowseToggle = async (enabled: boolean) => {
    const previous = publicBrowse;
    setPublicBrowse(enabled); // optimistic
    setSavingPublicBrowse(true);
    try {
      const result = await setOrganizationPublicBrowse(organizationId, enabled);
      setPublicBrowse(result.publicBrowseEnabled);
      toast.success(
        `Public catalogue ${enabled ? "opened" : "closed"} for ${
          stats?.organization?.name ?? "this organization"
        }.`,
        { position: "top-right" }
      );
    } catch (err: any) {
      setPublicBrowse(previous); // roll back
      toast.error(
        getErrorMessage(err, "Could not update public browsing."),
        { position: "top-right" }
      );
    }
    setSavingPublicBrowse(false);
  };

  const handlePlatformChange = async (platform: PlatformKey) => {
    if (!stats || platform === stats.organization.platform) return;
    const name = stats.organization.name;
    if (
      !window.confirm(
        `Move ${name} to the ${PLATFORM_LABELS[platform]} platform? Their staff will be sent to the ${PLATFORM_LABELS[platform]} link when they sign in.`
      )
    )
      return;
    setSavingPlatform(true);
    try {
      const result = await setOrganizationPlatform(organizationId, platform);
      setStats((prev) =>
        prev
          ? { ...prev, organization: { ...prev.organization, platform: result.platform } }
          : prev
      );
      toast.success(`${name} moved to ${PLATFORM_LABELS[platform]}.`, {
        position: "top-right",
      });
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Could not change the platform."), {
        position: "top-right",
      });
    }
    setSavingPlatform(false);
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Organization not found.</Typography>
      </Box>
    );
  }

  const org = stats.organization;
  const byStatus = stats.bookings?.byStatusInWindow || {};
  const statusTotal = Object.values(byStatus).reduce((a, b) => a + b, 0);
  const cancelled = (byStatus.CANCELLED ?? 0) + (byStatus.CANCELED ?? 0);
  const cancelRate = statusTotal ? Math.round((cancelled / statusTotal) * 100) : 0;
  const cancelIsHigh = cancelRate >= 20;
  const orderedKeys = featureKeys.length
    ? featureKeys
    : Object.keys(features || {});
  const avatarTone: Tone = org.isDeleted ? "slate" : org.platform === "CLINIC" ? "teal" : "blue";

  return (
    <Box sx={{ maxWidth: 1240, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/superadmin/organizations")}
        sx={{ textTransform: "none", mb: 1.5, color: SA.textMuted, fontWeight: 500 }}
      >
        All organizations
      </Button>

      <Paper elevation={0} sx={{ ...saCardSx, mb: 3, overflow: "hidden" }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            gap: { xs: 2, sm: 2.5 },
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1, minWidth: 0 }}>
            <Box
              aria-hidden
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
                fontSize: "1.25rem",
                color: TONES[avatarTone].fg,
                bgcolor: TONES[avatarTone].bg,
                border: `1px solid ${TONES[avatarTone].border}`,
              }}
            >
              {initialsOf(org.name)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography
                  component="h1"
                  sx={{ fontWeight: 700, fontSize: { xs: "1.35rem", sm: "1.6rem" }, color: SA.text, letterSpacing: "-0.01em" }}
                >
                  {org.name}
                </Typography>
                <PlatformChip platform={org.platform} businessType={org.businessType} />
                {org.isDeleted && <StatusPill tone="red">Closed</StatusPill>}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5, mt: 0.75 }}>
                <MetaItem icon={<PublicOutlinedIcon />}>+{org.country}</MetaItem>
                <MetaItem icon={<ScheduleOutlinedIcon />}>{org.timezone}</MetaItem>
                <MetaItem icon={<CalendarTodayOutlinedIcon />}>
                  Joined {format(new Date(org.createdAt), "d MMM yyyy")} · {org.ageDays} day
                  {org.ageDays === 1 ? "" : "s"} ago
                </MetaItem>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, auto)",
              columnGap: { xs: 3, sm: 4 },
              px: { md: 3 },
              py: { xs: 1.5, md: 0.5 },
              borderLeft: { md: `1px solid ${SA.divider}` },
              borderTop: { xs: `1px solid ${SA.divider}`, md: 0 },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <HeaderFact label="Subscription">
              <SubscriptionStatusChip summary={org.subscription} />
            </HeaderFact>
            <HeaderFact label="Clients">{stats.people.clients.toLocaleString()}</HeaderFact>
            <HeaderFact label="Upcoming">{stats.bookings.upcoming.toLocaleString()}</HeaderFact>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "200px minmax(0, 1fr)" },
          alignItems: "start",
        }}
      >
        <SaSectionNav title="Organization" items={TABS} value={tab} onChange={openTab} />
        <Box sx={{ minWidth: 0 }}>
          {tab === "overview" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.1rem", color: SA.text }}>
                    Bookings
                  </Typography>
                  <Typography sx={{ fontSize: "0.84rem", color: SA.textMuted }}>
                    The last {stats.windowDays} days
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={days}
                  onChange={(_, value) => value && setDays(value)}
                  sx={pillToggleSx}
                >
                  {WINDOWS.map((option) => (
                    <ToggleButton key={option} value={option}>
                      {option} days
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <KpiGrid>
                <KpiCard
                  label="Bookings"
                  icon={<EventAvailableOutlinedIcon />}
                  tone="blue"
                  value={stats.bookings.inWindow.toLocaleString()}
                  caption={`${stats.bookings.total.toLocaleString()} all time`}
                />
                <KpiCard
                  label="Upcoming"
                  icon={<UpcomingOutlinedIcon />}
                  tone="violet"
                  value={stats.bookings.upcoming.toLocaleString()}
                  caption="From now on"
                />
                <KpiCard
                  label="Last booked"
                  icon={<HistoryOutlinedIcon />}
                  tone="teal"
                  value={
                    stats.bookings.lastCreatedAt
                      ? formatDistanceToNow(new Date(stats.bookings.lastCreatedAt), { addSuffix: true })
                      : "Never"
                  }
                />
                <KpiCard
                  label="Cancellation rate"
                  icon={<EventBusyOutlinedIcon />}
                  tone={cancelIsHigh ? "red" : "green"}
                  highlight={cancelIsHigh}
                  value={`${cancelRate}%`}
                  captionTone={cancelIsHigh ? "red" : "green"}
                  caption={
                    <>
                      {cancelIsHigh ? (
                        <WarningAmberIcon sx={{ fontSize: 15 }} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                      )}
                      {cancelIsHigh ? "High, worth a call" : "Healthy"} · {cancelled} of {statusTotal}
                    </>
                  }
                />
              </KpiGrid>

              <SaCard
                title="Booking status"
                subtitle={`How the ${statusTotal.toLocaleString()} bookings in this window ended up`}
                icon={<DonutSmallOutlinedIcon />}
              >
                <StatusBreakdown byStatus={byStatus} />
              </SaCard>

              <Box
                sx={{
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                }}
              >
                <SaCard title="People" icon={<PeopleAltOutlinedIcon />} tone="violet">
                  <MiniStat icon={<PeopleAltOutlinedIcon />} tone="violet" label="Clients" value={stats.people.clients} />
                  <MiniStat icon={<BadgeOutlinedIcon />} tone="blue" label="Staff" value={stats.people.staff} divider />
                  <MiniStat icon={<SportsOutlinedIcon />} tone="teal" label="Coaches" value={stats.people.coaches} divider />
                </SaCard>
                <SaCard title="Setup" icon={<StorefrontOutlinedIcon />} tone="teal">
                  <MiniStat icon={<StorefrontOutlinedIcon />} tone="teal" label="Businesses" value={stats.setup.businesses} />
                  <MiniStat icon={<MeetingRoomOutlinedIcon />} tone="blue" label="Units" value={stats.setup.units} divider />
                  <MiniStat icon={<Inventory2OutlinedIcon />} tone="amber" label="Resources" value={stats.setup.resources} divider />
                  <MiniStat icon={<ShoppingBagOutlinedIcon />} tone="violet" label="Products" value={stats.setup.products} divider />
                </SaCard>
                <SaCard title="Modules" icon={<ViewModuleOutlinedIcon />} tone="amber">
                  <MiniStat
                    icon={<GroupsOutlinedIcon />}
                    tone="blue"
                    label="Classes"
                    caption={`${stats.classes.enrollments} enrollments`}
                    value={stats.classes.offerings}
                  />
                  <MiniStat
                    icon={<LocalOfferOutlinedIcon />}
                    tone="violet"
                    label="Packages"
                    caption={`${stats.packages.sold} sold`}
                    value={stats.packages.offered}
                    divider
                  />
                  <MiniStat
                    icon={<Inventory2OutlinedIcon />}
                    tone="amber"
                    label="Stock moves"
                    caption={`In the last ${stats.windowDays} days`}
                    value={stats.stock.movementsInWindow}
                    divider
                  />
                </SaCard>
              </Box>
            </Box>
          )}

          {tab === "subscription" && (
            <SubscriptionPanel
              organizationId={organizationId}
              organizationName={org.name}
              platform={org.platform}
              onChanged={(result) =>
                setStats((prev) =>
                  prev
                    ? {
                        ...prev,
                        organization: {
                          ...prev.organization,
                          subscription: result.current,
                        },
                      }
                    : prev
                )
              }
            />
          )}

          {tab === "messages" && <NotificationPanel organizationId={organizationId} />}

          {tab === "settings" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <SaCard
                title="Features"
                subtitle="Turning a feature off hides it. Loyalty points, classes and stock records all survive, and switching it back on restores access exactly as it was."
                icon={<ExtensionOutlinedIcon />}
                tone="violet"
                flush
              >
                {orderedKeys.map((key, index) => (
                  <SettingRow
                    key={key}
                    divider={index > 0}
                    icon={FEATURE_ICONS[key] ?? <AppsOutlinedIcon />}
                    tone={features[key] ? "violet" : "slate"}
                    title={featureLabel(key)}
                    description={FEATURE_DESCRIPTIONS[key] || "Controls the matching sections of the backoffice."}
                    control={
                      <Switch
                        checked={!!features[key]}
                        disabled={savingKey === key}
                        onChange={(e) => handleToggle(key, e.target.checked)}
                        slotProps={{ input: { "aria-label": featureLabel(key) } }}
                      />
                    }
                  />
                ))}
              </SaCard>

              <SaCard title="Access" subtitle="Where this organization's staff sign in, and who can see its catalogue." icon={<LanguageOutlinedIcon />} flush>
                <SettingRow
                  icon={<AppsOutlinedIcon />}
                  tone="blue"
                  title="Signed up through"
                  description="Decides which link this organization's staff use, and which packages they're offered."
                  control={
                    <TextField
                      select
                      size="small"
                      value={org.platform}
                      disabled={savingPlatform}
                      onChange={(e) => handlePlatformChange(e.target.value as PlatformKey)}
                      sx={{ minWidth: 170 }}
                      slotProps={{ htmlInput: { "aria-label": "Platform" } }}
                    >
                      {PLATFORM_KEYS.map((key) => (
                        <MenuItem key={key} value={key}>
                          {PLATFORM_LABELS[key]}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />
                <SettingRow
                  divider
                  icon={<PublicOutlinedIcon />}
                  tone={publicBrowse ? "green" : "slate"}
                  title="Public browsing"
                  description="Lets visitors browse this organization's catalogue and schedule without signing in. Visitors who already loaded it may keep seeing it for up to 5 minutes after it's turned off."
                  control={
                    <Switch
                      checked={publicBrowse}
                      disabled={savingPublicBrowse}
                      onChange={(e) => handlePublicBrowseToggle(e.target.checked)}
                      slotProps={{ input: { "aria-label": "Public browsing" } }}
                    />
                  }
                />
              </SaCard>
            </Box>
          )}

          {tab === "danger" && (
            <OrganizationDangerZone
              organizationId={organizationId}
              organizationName={org.name}
              isDeleted={org.isDeleted}
              onDeletedChange={(isDeleted) =>
                setStats((prev) =>
                  prev ? { ...prev, organization: { ...prev.organization, isDeleted } } : prev
                )
              }
              onRemoved={() => navigate("/superadmin/organizations", { replace: true })}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationDetails;
